import { useEffect, useState, useContext } from 'react';
import _ from 'lodash';
import mercuryContext from 'ConnectionManager/mercuryContext';
import gradableByDeploymentQuery from './gradableByDeploymentQuery.gql';
import { ACTIVITY } from '../../constants';

// Exported as an object so jest can mock local functions.
export const gradeFunctions = {
  /* Given a deployment and cohort, fetch all the deployment's gradable components
   * Throws on graphql errors.
   */
  getGradablesByDeployment: async (client, variables) => {
    const fieldsToObject = (fieldCollection) =>
      _.chain(fieldCollection).keyBy('fieldName').mapValues('fieldValue').value();

    const componentResults = await client.query({
      query: gradableByDeploymentQuery,
      variables,
    });

    if (componentResults.error) {
      throw new Error(componentResults.error);
    }

    const gradables = _.get(
      componentResults,
      'data.cohortById.deployment.0.manualGradingComponents',
    ).map(({ componentConfigurationFields, parentConfigurationFields, ...rest }) => ({
      ...rest,
      componentConfiguration: fieldsToObject(componentConfigurationFields),
      parentConfiguration: fieldsToObject(parentConfigurationFields),
    }));
    return gradables;
  },

  /* Return only Gradables that match our current interactive ID
   * (supplied previously as walkableId)
  */
  filterGradablesByInteractive: (gradables, interactiveId) =>
    gradables.filter(({ parentId }) => parentId === interactiveId),

  /* High level driver to construct the final paired gradable objects from the hook's parameters
  */
  getManualGradesByWalkable: async (client, { walkableId, walkableType, cohortId, deploymentId }) => {
    if (walkableType === ACTIVITY) {
      throw new Error('querying manual grades via activity is not yet implemented');
    }
    const allGradablesByDeployment = await gradeFunctions.getGradablesByDeployment(client, { cohortId, deploymentId });
    const gradablesThatMatchCurrentInteractiveID = gradeFunctions.filterGradablesByInteractive(
      allGradablesByDeployment,
      walkableId,
    );
    return gradablesThatMatchCurrentInteractiveID;
  },

  /* An effect that returns student grades paired with the gradable component
   * from which they originate.
   */
  useStudentGradeSummary: ({ cohortId, deploymentId, walkableId, walkableType }) => {
    const [results, setResults] = useState({ loading: true, data: {}, error: null });
    // Take the apolloClient currently in context
    const { apolloClient } = useContext(mercuryContext);

    const compareFn = (a, b) => {
      const activityA = a.componentConfiguration.title;
      const activityB = b.componentConfiguration.title;
      return (activityA || '').localeCompare(activityB || '', undefined, { numeric: true });
    };

    useEffect(
      () => {
        setResults({ loading: true, data: {}, error: null });
        if (!cohortId || !deploymentId || !walkableId || !walkableType) {
          // we're not ready to do anything.  Just return.
          return () => { };
        }
        gradeFunctions
          .getManualGradesByWalkable(apolloClient, { walkableId, walkableType, cohortId, deploymentId })
          .then((gradables) => setResults({ loading: false, data: gradables && gradables.sort(compareFn), error: null }))
          .catch((error) => setResults({ loading: false, data: {}, error }));
        return () => { };
      },
      [cohortId, deploymentId, walkableId, walkableType, apolloClient],
    );
    return results;
  },

};

export default gradeFunctions.useStudentGradeSummary;
